/**
 * Lazy loads the standalone Vue player modal (components/PlayerModal.js) and the value-trend
 * chart (apps/TrendModalApp.js) for the Alpine rankings app. Nothing is fetched until a
 * visitor shows intent, so the rankings pages keep loading without Vue or either app.
 */

/**
 * @type {Promise<object>|null} Resolves to the PlayerModalApp module; shared by every caller so
 * the scripts are only ever requested once
 */
let playerModalAppPromise = null;

/**
 * Loads the global Vue build unless the page already has it (guest pages load it for the
 * subscription app in the footer)
 *
 * @param {string} vueUrl
 * @return {Promise<void>}
 */
const loadVue = function (vueUrl) {
    if (window.Vue) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = vueUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Vue for the player modal'));
        document.head.appendChild(script);
    });
};

/**
 * Loads Vue and then the player modal app, which reads Vue off of the window as it is evaluated
 *
 * @param {object} config - `appData.playerModal`
 * @param {string} config.vueUrl
 * @param {string} config.scriptUrl
 * @return {Promise<object>} The PlayerModalApp module
 */
export const loadPlayerModalApp = function (config) {
    if (!playerModalAppPromise) {
        playerModalAppPromise = loadVue(config.vueUrl)
            .then(() => import(config.scriptUrl))
            .catch(err => {
                // Let the next attempt start over instead of replaying the failure
                playerModalAppPromise = null;
                throw err;
            });
    }
    return playerModalAppPromise;
};

/**
 * @type {Promise<object>|null} Resolves to the TrendModalApp module
 */
let trendModalAppPromise = null;

/**
 * Loads Vue and then the trend chart app. Separate from the player modal so opening
 * a player does not download the chart, and opening a trend does not download the profile.
 *
 * @param {object} config - `appData.trendModal`
 * @param {string} config.vueUrl
 * @param {string} config.scriptUrl
 * @return {Promise<object>} The TrendModalApp module
 */
export const loadTrendModalApp = function (config) {
    if (!trendModalAppPromise) {
        trendModalAppPromise = loadVue(config.vueUrl)
            .then(() => import(config.scriptUrl))
            .catch(err => {
                trendModalAppPromise = null;
                throw err;
            });
    }
    return trendModalAppPromise;
};
